// src/redux/slices/LoanKycApprovalSlice.js
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";

const API_URL = process.env.REACT_APP_API_URL;

// Helper to normalize errors
const getErrorMessage = (error, fallback = "Something went wrong") => {
  return (
    error?.response?.data?.message ||
    error?.response?.data?.error ||
    error?.message ||
    fallback
  );
};

// 1️⃣ Fetch Loan Details
export const fetchLoanKycApproval = createAsyncThunk(
  "loanKyc/fetchLoanKycApproval",
  async (loanId, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${API_URL}/get-loan-by/${loanId}`);
      if (!response.data) throw new Error("Invalid API response");
      return response.data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, "Failed to fetch loan details"));
    }
  }
);

// 2️⃣ Fetch Loan KYC Documents (by userId)
export const fetchLoanKycDocuments = createAsyncThunk(
  "loanKyc/fetchLoanKycDocuments",
  async (userId, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${API_URL}/get-loan-kyc`, {
        params: { userId },
      });
      return response.data.documents || [];
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, "Failed to fetch Loan KYC documents"));
    }
  }
);

// 3️⃣ Reject Loan KYC Document (PUT instead of POST)
export const rejectLoanKycDoc = createAsyncThunk(
  "loanKyc/rejectLoanKycDoc",
  async (docId, { rejectWithValue }) => {
    try {
      const response = await axios.put(`${API_URL}/kyc-docs/${docId}/reject`);
      return { docId, ...response.data };
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, "Failed to reject document"));
    }
  }
);

// 4️⃣ Approve Loan KYC Document (PUT instead of POST)
export const approveLoanKycDoc = createAsyncThunk(
  "loanKyc/approveLoanKycDoc",
  async (docId, { rejectWithValue }) => {
    try {
      const response = await axios.put(`${API_URL}/kyc-docs/${docId}/approve`);
      return { docId, ...response.data };
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, "Failed to approve document"));
    }
  }
);

const loanKycApprovalSlice = createSlice({
  name: "loanKycApproval",
  initialState: {
    loan: null,
    kycDocs: [],
    loading: false,
    error: null,
    status: "idle", // idle | loading | succeeded | failed
  },
  reducers: {
    clearLoanKycDetails: (state) => {
      state.loan = null;
      state.kycDocs = [];
      state.loading = false;
      state.error = null;
      state.status = "idle";
    },
  },
  extraReducers: (builder) => {
    builder
      // Loan details
      .addCase(fetchLoanKycApproval.pending, (state) => {
        state.loading = true;
        state.status = "loading";
        state.error = null;
      })
      .addCase(fetchLoanKycApproval.fulfilled, (state, action) => {
        state.loading = false;
        state.status = "succeeded";
        state.loan = action.payload;
      })
      .addCase(fetchLoanKycApproval.rejected, (state, action) => {
        state.loading = false;
        state.status = "failed";
        state.error = action.payload;
      })

      // Loan KYC docs
      .addCase(fetchLoanKycDocuments.pending, (state) => {
        state.loading = true;
        state.status = "loading";
        state.error = null;
      })
      .addCase(fetchLoanKycDocuments.fulfilled, (state, action) => {
        state.loading = false;
        state.status = "succeeded";
        state.kycDocs = action.payload;
      })
      .addCase(fetchLoanKycDocuments.rejected, (state, action) => {
        state.loading = false;
        state.status = "failed";
        state.error = action.payload;
      })

      // Reject Loan KYC
      .addCase(rejectLoanKycDoc.fulfilled, (state, action) => {
        state.kycDocs = state.kycDocs.map((doc) =>
          doc.id === action.payload.docId ? { ...doc, status: "Rejected" } : doc
        );
      })
      .addCase(rejectLoanKycDoc.rejected, (state, action) => {
        state.error = action.payload;
      })

      // Approve Loan KYC
      .addCase(approveLoanKycDoc.fulfilled, (state, action) => {
        state.kycDocs = state.kycDocs.map((doc) =>
          doc.id === action.payload.docId ? { ...doc, status: "Approved" } : doc
        );
      })
      .addCase(approveLoanKycDoc.rejected, (state, action) => {
        state.error = action.payload;
      });
  },
});

export const { clearLoanKycDetails } = loanKycApprovalSlice.actions;
export default loanKycApprovalSlice.reducer;
